import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { AlertHeader, Button, H3, LoaderSpin, ModalCloseButton, ModalContent, ModalFactory, ModalFooter, ModalText, Text } from 'components';
import { theme, toRem } from 'design';
import routeConstants from 'routes/routeConstants';
import { getCalculatedTipsRequest, selectCalculatorPayees, setCalculatorPayees, setChangeLogEntries } from 'store/TipCalculator';
import { ICalculatorRules, IDaySegment, IHoldbackFixed, IHoldbackPercent, IPOSResults, ITipCalculatorChangeLogEntry, ITipCalculatorResult } from 'types';
import { capitalize } from 'utils/strings';
import { buildTipCalculatorPayeeFromPOSData, buildTipCalculatorRequests, getSharedPOSJobTitles, TipCalculationValidationError } from 'utils/tipCalculator';

interface ICalculateProcessingModalProps {
  siteId: string;
  siteHoldbackSettings: IHoldbackFixed | IHoldbackPercent;
  businessDate: string;
  tipCalcRules: ICalculatorRules;
  segments: IDaySegment[];
  posDataResults: IPOSResults;
  creditCardPercentFee: number;
  changeLogEntries?: ITipCalculatorChangeLogEntry[];
  onClose(): void;
  onSuccess?: () => void;
}

interface IContentProps {
  businessDate: string;
  message: string;
  stepMessage: string;
  status: 'loading' | 'error' | 'validationError' | 'success';
  buttonText: string;
  errors?: any;
  onClick?(): void;
  onClose(): void;
}

const Layout = styled.section`
  position: relative;
  z-index: ${theme.zIndex.modal + 2};
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  max-width: ${toRem(440)};
  height: auto;
  margin: ${toRem(theme.layout.gutter.large)} 0;
  padding: ${toRem(theme.modal.spacing.large)};
  border-radius: ${toRem(22)};
  background-color: ${theme.color.white};
  box-shadow: ${theme.shadow.modal};

  h3.modal-header-text {
    font-size: ${toRem(20)};
  }

  h3.modal-header-text-error {
    color: ${theme.color.danger.medium};
    margin-top: ${toRem(22)};
  }

  .modal-text {
    margin-top: ${toRem(12)};
    margin-bottom: ${toRem(28)};
  }

  .modal-step-message {
    margin-top: ${toRem(28)};
    text-align: center;
  }

  header {
    padding-bottom: 0;

    div {
      margin-bottom: 0;
    }
  }

  footer {
    padding-top: ${toRem(22)};

    button {
      width: 100%;
    }
  }
`;

const ErrorDetails = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
  padding: ${toRem(22)};
  border-radius: ${toRem(11)};
  background-color: ${theme.color.neutral[50]};
  margin-top: ${toRem(12)};
  text-align: left;

  h3 {
    margin-bottom: ${toRem(8)};
  }

  &.validation-error {
    h3 {
      margin-bottom: ${toRem(8)};
      text-transform: capitalize;
    }

    p {
      font-size: ${toRem(14)};
    }
  }
`;

const JobList = styled.ul`
  padding: ${toRem(12)} ${toRem(20)};
`;

const JobListItem = styled.li`
  text-transform: capitalize;
  font-size: ${toRem(14)};
  margin: ${toRem(4)} 0;
`;

const Content: React.FC<IContentProps> = ({ businessDate, buttonText, message, stepMessage, status, errors, onClick, onClose }) => (
  <Layout data-cy="integration-data-procesing-modal">
    {status !== 'success' && <ModalCloseButton onClick={onClose} />}
    <ModalContent>
      <H3 className="modal-header-text">{dayjs(businessDate).format('MMMM Do, YYYY')}</H3>
      {status === 'validationError' && <H3 className="modal-header-text-error">{errors instanceof TipCalculationValidationError && errors.errors.length === 1 ? `${errors.errors.length} Warning` : `${errors.errors.length} Warnings`}</H3>}
      <ModalText className="modal-text">{message}</ModalText>
      {status === 'loading' && <LoaderSpin size={60} stroke={5} color={theme.color.secondary[400]} />}
      {status === 'error' && <AlertHeader />}
      {status === 'success' && <AlertHeader icon="Success" iconFill={theme.color.secondary[400]} />}
      {stepMessage && <H3 className="modal-step-message">{stepMessage}</H3>}
      {errors instanceof TipCalculationValidationError &&
        errors.errors.map((error) => (
          <ErrorDetails data-cy="tip-calc-validation-errors" className="validation-error">
            <H3 fontWeight="bold">{error.type.replaceAll('_', ' ')}</H3>
            <Text>{capitalize(error.message)}</Text>
            <JobList>
              {error.jobs.map((job) => (
                <JobListItem>{job}</JobListItem>
              ))}
            </JobList>
          </ErrorDetails>
        ))}

      {errors && !(errors instanceof TipCalculationValidationError) && errors.length && errors[0].msg && (
        <ErrorDetails data-cy="integration-data-procesing-errors">
          <H3 fontWeight="bold">Details</H3>
          <Text>{errors[0].msg}</Text>
        </ErrorDetails>
      )}
    </ModalContent>
    <ModalFooter columns={1}>
      <Button data-cy="integration-data-action-btn" label={buttonText} type="button" appearance="primary" size="large" onClick={onClick} isProcessing={false} disabled={status === 'loading'} />
    </ModalFooter>
  </Layout>
);

export const CalculateProcessingModal: React.FC<ICalculateProcessingModalProps> = ({
  businessDate,
  siteId,
  siteHoldbackSettings,
  creditCardPercentFee,
  tipCalcRules,
  segments,
  posDataResults: integrationDataResults,
  changeLogEntries = [],
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isMounted = useRef(false);
  const currentCalcPayees = useSelector(selectCalculatorPayees());

  const ContentProps: Record<string, Partial<IContentProps>> = {
    calculateTips: {
      message: 'Hang tight, this may take a moment.',
      stepMessage: 'Calculating tips...',
      status: 'loading',
      buttonText: 'Review Payouts',
    },
    calculateSuccess: {
      message: 'All systems go!',
      stepMessage: 'Success!',
      status: 'success',
      buttonText: 'Review Payouts',
      onClick: () => history.push(routeConstants.private.makePaymentCalculatorReview),
    },
    calculateError: {
      stepMessage: 'An error has occurred calculating tips',
      status: 'error',
      buttonText: 'Try Again',
      onClick: () => calculateTips(),
    },
  };

  const [contentProps, setContentProps] = useState<Partial<IContentProps>>(ContentProps.calculateTips);

  const onTipCalcSuccess = (tipCalcResults: ITipCalculatorResult[]) => {
    try {
      let payees = buildTipCalculatorPayeeFromPOSData(tipCalcResults, integrationDataResults, siteHoldbackSettings, getSharedPOSJobTitles(tipCalcRules));

      /**
       * because payees is a complete override of existing payees, we need to merge modified fields
       * from the currentCalcPayees into the new payees array
       */
      if (currentCalcPayees?.length) {
        payees = payees.map((payee) => {
          const currentPayee = currentCalcPayees.find((p) => p.provider?.employeeId === payee.provider?.employeeId);
          if (!currentPayee) return payee;

          return {
            ...payee,
            note: currentPayee.note,
            holdbackAmount: currentPayee.holdbackAmount,
            net: payee.gross - currentPayee.holdbackAmount,
            employee: currentPayee.employee,
          };
        });
      }

      dispatch(setChangeLogEntries(changeLogEntries));
      dispatch(setCalculatorPayees(payees));
      onSuccess?.();

      if (isMounted.current) setContentProps(ContentProps.calculateSuccess);
    } catch (err) {
      // TODO: sync with team to figure out how to handle errors here
      //   - should we manually log this error but show a generic error message to the user?

      // eslint-disable-next-line no-console
      console.log('error building tip calculator payee from labor ticket data', err);

      if (isMounted.current) setContentProps({ ...ContentProps.calculateError, errors: [err] });
    }
  };

  const onTipCalcFailure = (errors: any) => {
    // eslint-disable-next-line no-console
    console.log('onTipCalcFailure', errors);

    if (isMounted.current) setContentProps({ ...ContentProps.calculateError, errors });
  };

  const calculateTips = () => {
    const tipCalcRequests = buildTipCalculatorRequests(integrationDataResults, tipCalcRules, segments);
    if (isMounted.current) setContentProps(ContentProps.calculateTips);
    dispatch(
      getCalculatedTipsRequest(
        tipCalcRequests.filter((req) => req.employees.length > 0),
        integrationDataResults,
        creditCardPercentFee,
        onTipCalcSuccess,
        onTipCalcFailure
      )
    );
  };

  // initialize by loading data & mounting the component
  useEffect(() => {
    isMounted.current = true;
    calculateTips();
  }, [siteId]);

  return (
    <ModalFactory customModal onClose={onClose} isActive closeOnOutSideClick={false}>
      <Content
        businessDate={businessDate}
        buttonText={contentProps.buttonText}
        errors={contentProps.errors}
        message={contentProps.message}
        onClick={contentProps.onClick}
        onClose={onClose}
        stepMessage={contentProps.stepMessage}
        status={contentProps.status}
      />
    </ModalFactory>
  );
};
